import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MapPin, X } from 'lucide-react';
import client from '../api/client';
import echo from '../api/echo';
import { useMarkAsRead, type Alert } from '../hooks/useAlerts';
import { useT } from '@/i18n/useT';

/**
 * Vigía global: sondea las alertas de "agente_localizado" (agente que activa su localización) y
 * muestra un modal cuando llega una nueva, con opción de verla en vivo sobre el plano.
 *
 * Se monta una vez en el Layout. El sondeo (~8 s) es el que marca la latencia del aviso; para
 * tiempo real instantáneo habría que activar Reverb (ahora BROADCAST_CONNECTION=log).
 */
export default function LocalizacionAlertModal() {
    const t = useT();
    const navigate = useNavigate();
    const markRead = useMarkAsRead();
    const qc = useQueryClient();
    const [current, setCurrent] = useState<Alert | null>(null);
    const shown = useRef<Set<number>>(new Set());

    const { data } = useQuery<{ data: Alert[] }>({
        queryKey: ['alertas-localizacion'],
        queryFn: async () => {
            const { data } = await client.get('/alertas?tipo=agente_localizado&leida=false&limit=10');
            return data;
        },
        refetchInterval: 8000, // respaldo por si el websocket no está disponible
    });

    // Tiempo real (Reverb): al recibir el evento, refrescamos al instante la lista de alertas →
    // el efecto de abajo abre el modal sin esperar al sondeo. Reutiliza la misma lógica de "mostrar".
    useEffect(() => {
        const ch = echo.channel('alertas');
        ch.listen('.AgenteLocalizado', () => {
            qc.invalidateQueries({ queryKey: ['alertas-localizacion'] });
        });
        return () => { echo.leave('alertas'); };
    }, [qc]);

    useEffect(() => {
        if (current) return;
        const alertas = data?.data ?? [];
        // Las más antiguas primero, para no saltarnos ninguna.
        const pendiente = [...alertas].reverse().find((a) => !shown.current.has(a.id));
        if (pendiente) {
            shown.current.add(pendiente.id);
            setCurrent(pendiente);
        }
    }, [data, current]);

    if (!current) return null;

    const cerrar = (alert: Alert) => {
        markRead.mutate(alert.id);
        setCurrent(null);
    };

    const verEnVivo = () => {
        const alert = current;
        cerrar(alert);
        if (alert.planta_id) navigate(`/plantas/${alert.planta_id}?tab=live`);
    };

    return (
        <div
            className="fixed inset-0 z-alert flex items-center justify-center bg-black/60 p-4"
            onClick={() => cerrar(current)}
        >
            <div
                className="bg-card text-foreground rounded-lg shadow-xl max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-green-600" aria-hidden /> {t('localizacion.alertas.localizacionActivada')}
                    </h2>
                    <button onClick={() => cerrar(current)} className="p-1 rounded hover:bg-muted" aria-label={t('comun.acciones.cerrar')}>
                        <X className="h-5 w-5" aria-hidden />
                    </button>
                </div>
                <div className="p-5 space-y-5">
                    <p className="text-sm">{current.mensaje}</p>
                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={() => cerrar(current)}
                            className="px-4 py-2 rounded border hover:bg-muted text-sm"
                        >
                            {t('localizacion.alertas.descartar')}
                        </button>
                        <button
                            onClick={verEnVivo}
                            disabled={!current.planta_id}
                            className="px-4 py-2 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 text-sm"
                        >
                            {t('localizacion.alertas.verEnVivo')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
